import { Button, DakenService, Instagram, NumbersHelper, SocialNetworksAvailables, ToastHelper, UserHelper } from '@hub/shared'

import { SqDataLayerHelper, SqModalProfileCache } from '@squidit/react-css'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import './profile-cache.component.scoped.scss'

const NUMBERS_OF_MONTHS_TO_SHOW_NEW_TAG = 3
const SOCIAL_NETWORKS_AVAILABLE_FOR_CACHE = [
  'instagram',
  // 'youtube',
  // 'twitter',
  // 'tiktok'
] as SocialNetworksAvailables[]

export interface Cache {
  profileId: string
  contentType: string
  contentValue: string
  createdAt?: string
  updatedAt?: string
}

export type CacheField = 'posts' | 'reels' | 'stories' | 'videos' | 'tweets'

interface Props {
  socialNetworkObject: Instagram
  fieldsCache: CacheField[]
  type: SocialNetworksAvailables
}

export default ({ socialNetworkObject, fieldsCache, type }: Props) => {
  const sqDataLayerHelper = useMemo(() => new SqDataLayerHelper(), [])
  const [cacheStatus, setCacheStatus] = useState<'empty' | 'complete' | 'incomplete' | null>(null)
  const [openModal, setOpenModal] = useState(false)
  const numbersHelper = useMemo(() => new NumbersHelper(), [])
  const dakenService = useMemo(() => new DakenService(), [])
  const toast = useMemo(() => new ToastHelper()?.toast, [])
  const userHelper = useMemo(() => new UserHelper(), [])
  const [cacheState, setCacheState] = useState<Record<CacheField, string>>({} as Record<CacheField, string>)
  const navigate = useNavigate()

  const { t } = useTranslation('profileCache')

  useEffect(() => {
    const query = new URLSearchParams(window.location.search)
    if (query.get('action') === 'updateCache' && query.get('profileId') === socialNetworkObject?.id) {
      setOpenModal(true)
    }
  }, [socialNetworkObject?.id])

  const buildCacheStatus = useMemo(() => {
    const defaultStructure = {
      background: 'var(--background_secondary)',
      button: {
        label: t('updateValues'),
        icon: 'fa-solid fa-diamond-exclamation gold mr-2',
        buttonProps: {
          label: 'updateValues',
          color: 'primary',
          borderColor: 'transparent',
          onClick: () => setOpenModal(true),
        },
      },
    }

    return {
      empty: defaultStructure,
      complete: {
        background: 'transparent',
        button: {
          label: t('updateValues'),
          icon: null,
          buttonProps: {
            label: 'updateValues',
            color: 'transparent',
            textColor: 'var(--primary_color)',
            borderColor: 'var(--primary_color)',
            onClick: () => setOpenModal(true),
          },
        },
      },
      incomplete: defaultStructure,
    }[cacheStatus || 'empty']
  }, [cacheStatus, t])

  const valueInCurrencyFormat = (value: number | null) => {
    if (!value) {
      return '--'
    }

    const integerNumber = Math.floor(value)
    const integerNumberFormatted = numbersHelper.formatStandardNumber({ lang: 'pt-BR', number: integerNumber })
    const decimalNumber = Math.round((value - integerNumber) * 100)

    return (
      <div className="value-formatted">
        <span className="integer-number">{integerNumberFormatted || '0'},</span>
        <span className="decimal-number">{decimalNumber < 10 ? `0${decimalNumber}` : decimalNumber || '00'}</span>
      </div>
    )
  }

  const getMonthDifference = useCallback(() => {
    const today = new Date()
    const referenceDate = new Date('2024/02/27')
    return today.getMonth() - referenceDate.getMonth() + 12 * (today.getFullYear() - referenceDate.getFullYear())
  }, [])

  useEffect(() => {
    const getStatusCache = () => {
      const isEmpty = !socialNetworkObject?.socialNetworkProfilesCache?.length
      const isIncomplete = socialNetworkObject?.socialNetworkProfilesCache?.some(({ contentValue }) => !contentValue)

      if (isEmpty) {
        setCacheStatus('empty')
      } else if (isIncomplete) {
        setCacheStatus('incomplete')
      } else {
        setCacheStatus('complete')
      }
    }

    getStatusCache()
  }, [socialNetworkObject])

  const onStateChange = useCallback((updatedState: Record<CacheField, string>) => {
    setCacheState(updatedState)
  }, [])

  const handleSubmit = useCallback(() => {
    const posts = numbersHelper?.convertCurrencyToNumber(cacheState?.posts) || null
    const stories = numbersHelper?.convertCurrencyToNumber(cacheState?.stories) || null
    const reels = numbersHelper?.convertCurrencyToNumber(cacheState?.reels) || null

    dakenService
      ?.putProfileCache({
        socialId: socialNetworkObject?.id,
        cache: {
          posts,
          stories,
          reels,
        },
      })
      .then(async (response) => {
        const user = await userHelper?.getUser()

        const newSocialNetworkObject = user?.[type === 'facebook' ? 'instagram' : type] as Instagram[]
        const indexToRemove = newSocialNetworkObject?.findIndex((field) => field?.id === socialNetworkObject?.id)
        if (indexToRemove !== undefined && indexToRemove !== -1) {
          newSocialNetworkObject?.splice(indexToRemove, 1)
        }

        const parseValues = response?.map((cache) => ({
          ...cache,
          contentValue: numbersHelper?.convertCurrencyToNumber(cache?.contentValue.toString()),
        }))

        newSocialNetworkObject?.push({
          ...socialNetworkObject,
          socialNetworkProfilesCache: parseValues,
        })

        await userHelper?.setUser({
          ...user,
          [type === 'facebook' ? 'instagram' : type]: newSocialNetworkObject,
        })

        toast?.success(t('success'), {
          closeButton: true,
          className: 'full text-bold',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          notOverClick: true,
          immediately: true,
        })
      })
      .catch(() => {
        toast?.error(t('error'), {
          closeButton: true,
          className: 'full',
          position: `${window.innerWidth > 991 ? 'top' : 'bottom'} center`,
          duration: 3600000,
          notOverClick: true,
          immediately: true,
        })
      })
      .finally(() => {
        userHelper?.getUser().then((user) => {
          sqDataLayerHelper.sendToDataLayer({
            event: 'hub_user_conversion',
            profile: socialNetworkObject?.username,
            socialNetwork: type,
            context: 'form',
            action: 'submit',
            description: 'socialnetworks_submit_cost',
            user: {
              squidId: user?.squidId,
            },
          })
        })

        setOpenModal(false)
        setCacheState({} as Record<CacheField, string>)
        const query = new URLSearchParams(window.location.search)
        query.delete('action')
        query.delete('profileId')
        navigate({ search: query.toString() })
      })
  }, [dakenService, t, toast, socialNetworkObject, type, userHelper, numbersHelper, cacheState, navigate, sqDataLayerHelper])

  return SOCIAL_NETWORKS_AVAILABLE_FOR_CACHE?.includes(type === 'facebook' ? 'instagram' : type) ? (
    <div className="profile-cache p-3">
      <header className="display-flex align-items-center">
        <div className="display-flex">
          {cacheStatus === 'empty' && getMonthDifference() <= NUMBERS_OF_MONTHS_TO_SHOW_NEW_TAG ? (
            <span className="tag-box mr-2 display-flex align-items-center background-blue white">{t('new')}</span>
          ) : null}
          <span className="h6 m-0 p-0">{t('valuesContent')}</span>
        </div>
        {cacheStatus && ['empty', 'incomplete'].includes(cacheStatus) ? (
          <div className="sub-header mt-2 small">{t('descriptionValues')}</div>
        ) : null}
      </header>
      <main className="cache-values-group my-3 display-flex justify-content-center">
        {fieldsCache?.length
          ? fieldsCache?.map((field, i) => (
              <div
                className={'cache-value px-3 py-1 display-flex justify-content-center align-items-center'}
                key={i}
                style={{ backgroundColor: buildCacheStatus?.background }}
              >
                <div className="title-field text-bold small">{t(`labels.${field}`)}</div>
                {valueInCurrencyFormat(
                  socialNetworkObject?.socialNetworkProfilesCache?.find((cache) => cache?.contentType === field)?.contentValue || null
                )}
              </div>
            ))
          : null}
      </main>
      <Button width="100%" size="sm" {...buildCacheStatus?.button?.buttonProps}>
        {buildCacheStatus?.button?.icon && <i className={buildCacheStatus?.button?.icon} />}
        {buildCacheStatus?.button?.label}
      </Button>

      <SqModalProfileCache
        socialNetworkObject={socialNetworkObject}
        fieldsCache={fieldsCache}
        open={openModal}
        onClose={() => setOpenModal(false)}
        type={type}
        onSubmit={() => handleSubmit()}
        cacheState={cacheState}
        onStateChange={onStateChange}
      />
    </div>
  ) : null
}
